import { Buffer } from "buffer";
import { api, apiBilletera } from "./api";
import axios from "axios";
import { useAuthStore } from "@/stores/auth";
import { useTalleresStore } from "@/stores/talleres";
import { PREVIEW_USER } from "@/utils/preview-data";

// Configuración del caché
const cache = {};
const CACHE_DURATION = 60000; // 1 minuto en milisegundos
let pageLoadTimestamp = Date.now();
let cachedUsuario = null;

// Funciones auxiliares para el manejo del caché
const isCacheValid = (key) => {
  const item = cache[key];
  if (!item) return false;
  return Date.now() - item.timestamp < CACHE_DURATION;
};

const setCacheItem = (key, value) => {
  cache[key] = { value, timestamp: Date.now() };
};

// Funciones de autenticación
export const recuperarClave = async (email) => {
  return api.post("/usuarios/v1/emailClave", { email });
}

export const actualizarClave = async (data) => {
  return api.put("/usuarios/v1/nuevaClave", data);
}

export const loginGoogle = async (data) => {
  const credencial = data.credential;
  const response = await api.post("/usuarios/v1/loginGoogle", { credencial });
  return response.data;
}

export const login = async (data) => {
  const { correo, contrasena, remember = true } = data;
  const token = `${correo}:${contrasena}`;
  const encodedToken = Buffer.from(token).toString("base64");

  try {
    const response = await api.get("/usuarios/v1/token", {
      headers: {
        Authorization: `Basic ${encodedToken}`,
      },
      params: {
        recordar: remember ? 1 : 0,
      },
    });

    console.log(response.data)

    return { login: true, usuario: response.data };
  } catch (e) {
    return { login: false, error: true };
  }
};

// Verificación de estado del alumno
const checkAlumnoStatus = async (email, taller = '') => {
  // Normalización del parámetro taller
  if (taller === 'dos') {
    taller = 'taller-2';
  }
  if (!taller || taller === 'uno') {
    taller = 'taller-1';
  }

  const cacheKey = `esAlumno${taller}`;

  // Verificar caché
  if (isCacheValid(cacheKey)) {
    return cache[cacheKey].value;
  }

  try {
    const url = `https://staging-strapi.orsai.org/api/alumnos/es-alumno?email=${email}`;
    const { data } = await axios.get(url);
    const isAlumno = data.talleres.includes(taller);
    setCacheItem(cacheKey, isAlumno);
    return isAlumno;
  } catch (e) {
    return false;
  }
};

// Manejo del caché de página
export const resetPageLoadCache = () => {
  pageLoadTimestamp = Date.now();
  cachedUsuario = null;
};

export const onUrlChange = () => {
  resetPageLoadCache();
};

// Obtención de información del usuario actual
export const miUsuario = async () => {
  const store = useAuthStore();
  const talleres = useTalleresStore();

  // Manejo de modo preview
  if (talleres.isPreview) {
    return PREVIEW_USER;
  }

  // Verificar caché de usuario
  if (cachedUsuario && Date.now() - pageLoadTimestamp < CACHE_DURATION) {
    return cachedUsuario;
  }

  try {
    const response = await api.get("/usuarios/v1");
    const usuario = response.data;

    // Configurar permisos y roles
    usuario.admin = [1, 3, 1384].includes(usuario.id);
    usuario.escuela = [];

    // Verificar si es docente (usar servicio Strapi con caché)
    try {
      // Usar el servicio Strapi que tiene caché en lugar de axios directo
      // Esto evita requests duplicados y aprovecha el caché existente
      const strapiService = (await import('./strapi.js')).default;
      const docentesResponse = await strapiService.get(`/api/docentes?filters[idUsuario][$eq]=${usuario.id}`);
      
      const docenteData = docentesResponse?.data?.[0];
      usuario.docente = !!docenteData;
      
      if (usuario.docente) {
        usuario.talleresDocente = docenteData?.talleres || [];
      } else {
        console.log('Usuario no es docente (no se encontró en Strapi)');
      }
    } catch (e) {
      console.error('Error al verificar si es docente en Strapi:', e);
      usuario.docente = false;
      usuario.talleresDocente = [];
    }

    // Verificar estado en talleres
    // usuario.alumno = await checkAlumnoStatus(usuario.email);
    // if (usuario.alumno) {
    //   usuario.escuela.push('taller-1');
    // }

    // usuario.alumno2 = await checkAlumnoStatus(usuario.email, 'dos');
    // if (usuario.alumno2) {
    //   usuario.escuela.push('taller-2');
    // }

    store.usuario = usuario;
    cachedUsuario = usuario;

    return usuario;
  } catch (e) {
    if (e.response && e.response.status === 403) {
      return null;
    }
    return null;
  }
};

// Funciones de gestión de usuarios
export const fetchUsers = async (data) => {
  const { porPag, pag } = data;
  return api.get(`/usuarios/v1/publico?pag=${pag}&porPag=${porPag}`);
};

export const fetchUserById = async (id) => {
  return (await api.get(`/usuarios/v1/publico/${id}`)).data;
};

export const fetchUserBySlug = async (slug) => {
  return api.get(`/usuarios/v1/publico-nombre/${slug}`);
};

export const createUser = async (data) => {
  const user = await api.post("/usuarios/v1/registro", data)
  return user.data;
};

// Funciones de actualización de perfil
export const actualizar = async (data) => {
  try {
    const response = await api.put(`/usuarios/v1`, data);


    resetPageLoadCache();
    return response;
  } catch (e) {
    return null;
  }
}

export const avatar = async (data) => {
  try {
    resetPageLoadCache();
    const response = await api.post(`/usuarios/v1/imagen`, data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (e) {
    return null;
  }
}

export const nuevaClave = async (data) => {
  try {
    const response = await api.put(`/usuarios/v1/nuevaClave`, data);
    return response.data;
  } catch (e) {
    return null;
  }
}

/**
 * Establecer contraseña de un usuario por email (admin).
 * Usado cuando el usuario ya existe pero no tenemos su contraseña (ej. agregar alumno al taller).
 * Requiere que el backend exponga PUT /usuarios/v1/admin/establecer-clave con { email, clave1 }.
 */
export const establecerClavePorEmail = async (email, clave1) => {
  const response = await api.put('/usuarios/v1/admin/establecer-clave', { email, clave1 });
  return response.data;
}

// Utilidades
export const idDesdeEmail = async (email) => {
  try {
    let encodedEmail = encodeURIComponent(email);
    encodedEmail = encodedEmail.replace(/\+/g, '%2B');
    const response = await api.get(`/usuarios/v1/usr-id-desde-email?e=${encodedEmail}`);
    return response.data;
  } catch (e) {
    return { id: null };
  }
}

export const verificarSuscripcionActiva = async () => {
  try {
    const response = await api.get(`/usuarios/v1/suscripcion/verificarSuscripcionActiva`);
    return response.data?.suscripcionActiva || false;
  } catch (e) {
    return { suscripcionActiva: false };
  }
}

export const bonosPorProyecto = async (simularIdUsr = null) => {
  let query = simularIdUsr ? `?simularIdUsr=${simularIdUsr}` : "";

  try {
    const response = await api.get(
      "/usuarios/v1/audiovisual/bonos-por-proyecto" + query
    );
    let bonos = response.data
      .filter((item) => item.bonos > 0)
      .filter((item) => ![11, 12, 13].includes(item.id));

    return bonos;
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const listadoUsuariosProyecto = async (proyecto) => {
  return (
    await api.get(
      `/usuarios/v1/audiovisual/${proyecto}/listado?orden=fecha&porPag=300`
    )
  ).data.usuarios;
};

export const cantidadBonos = async (proyecto) => {
  return (
    (await api.get(`/usuarios/v1/audiovisual/${proyecto}/cantidadBonos`)).data
      .total * 1
  );
};

export const cantidadBonosUsuario = async (proyecto) => {
  return (
    (await api.get(`/usuarios/v1/audiovisual/${proyecto}/cantidadBonosUsuario`))
      .data.total * 1
  );
};

export const cantidadUsuariosConBonos = async (proyecto) => {
  return (
    (
      await api.get(
        `/usuarios/v1/audiovisual/${proyecto}/cantidadUsuariosConBonos`
      )
    ).data.total * 1
  );
};

export const publicoProyecto = async (proyecto) => {
  return (await api.get(`/usuarios/v1/audiovisual/${proyecto}/publico`)).data;
};

export const obtenerProyecto = async (proyecto) => {
  return (await api.get(`/usuarios/v1/audiovisual/${proyecto}`)).data;
};

export const obtenerEncuesta = async (slug) => {
  return (await api.get(`/usuarios/v1/encuestas/${slug}`)).data;
};

export const obtenerResultadosEncuesta = async (slug) => {
  return (
    await api.get(
      `/usuarios/v1/encuestas/${slug}/resultados?categoria_usr=teatro1`
    )
  ).data;
};

export const votarEncuesta = async (slug, optionKey) => {
  return (
    await api.post(`/usuarios/v1/encuestas/${slug}/eleccion/${optionKey}`)
  ).data;
};

export const obtenerTickets = async (proyecto) => {
  return (await api.get(`/usuarios/v1/av/${proyecto}/tickets`)).data.tickets;
};

export const obtenerEstadisticasTickets = async (proyecto) => {
  return (
    await api.get(
      `/usuarios/v1/av/${proyecto.toLowerCase()}/estadisticasTickets`
    )
  ).data;
};

export const quemarTicket = async (proyecto, cantidad, funcion) => {
  return (
    await api.put(`/usuarios/v1/av/${proyecto}/quemarTicket`, {
      cantidad,
      funcion,
    })
  ).data;
};

export const obtenerBilleteras = async (simularIdUsr = null) => {
  let query = simularIdUsr ? `?simularIdUsr=${simularIdUsr}` : "";
  const tmp = (await apiBilletera.get(`/${query}`)).data;

  return tmp;
};

export const obtenerMovimientosBilletera = async (simularIdUsr = null) => {
  let query = simularIdUsr ? `?simularIdUsr=${simularIdUsr}` : "";
  return (await apiBilletera.get(`/movimientos${query}?porPag=100`)).data.movimientos;
};

export const obtenerMovimientosBilleteraConPaginado = async (data) => {
  const { porPag, pag } = data;
  return (await apiBilletera.get(`/movimientos?pag=${pag}&porPag=${porPag}`)).data;
};

export const balanceMoney = async () => {
  try {
    const result = (await api.get(`/usuarios/v1/creditos`)).data.creditos;

    return result;
  } catch (e) {
    return null;
  }
};

export const obtenerSolicitudesRetiro = async () => {
  let result = await api.get(`/usuarios/v1/cine/solicitudes-dividendos`);
  return result.data.metodo;
};

export const balance = async () => {
  let result = (await api.get("/usuarios/v1" + "/balance")).data;
  return result;
};

export const retirarDividendos = async (data) => {
  let result = await api.post(`/usuarios/v1/cine/dividendos`, data);
  return result.data;
}

export const crearCuentaYRetirar = async (data) => {
  let result = await apiBilletera.post(`/crear-retirar`, data);
  return result.data;
}

export const getUsuarios = async () => {
  try {
    const response = await api.get(`/usuarios/v1/`);

    return response;
  } catch (e) {
    console.error(e);
  }
}

export const getUsuariosPublicos = async (query) => {
  try {
    const response = await api.get('usuarios/v1/publico');

    return response.data.usuarios;
  } catch (e) {
    console.error(e);
  }
}

export const getUsuariosOrdenes = async (query) => {
  try {
    const response = await api.get('usuarios/v1/listado-usr-ordenes', { params: query });

    return response.data;
  } catch (e) {
    console.error(e);
  }
}

export const tokenDe = async (idUsr) => {
  try {
    const response = await api.get(`/usuarios/v1/tokenDe?idUsr=${idUsr}`);

    return response;
  } catch (e) {
    console.error(e);
  }
}

export const acreditar = async (data) => {
  try {
    const response = await apiBilletera.post(`/acreditar`, data);

    return response;
  } catch (e) {
    console.error(e);
  }
}